import type { components } from "@cntrl/api-client";
import { compactNumber } from "@cntrl/ui";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import styles from "./TopPlaylistList.module.scss";

type TopPlaylist = components["schemas"]["TopPlaylist"];

/**
 * The playlists list (the band's recent adds since ART-283) — ONE implementation, rendered by the Streaming page's playlists card and by the
 * dashboard's Spotify card under Audience.
 *
 * **Why it is shared.** Both read the SAME `TopPlaylist[]` off the same `SpotifyHero` (the shaper lands
 * `top_playlists` inside the spotify doc, and one `BuildSpotify` parses it for both surfaces), and both had
 * hand-rolled the same row: an identical `46px minmax(0,1fr) auto` grid, a 46px cover, an ellipsised name and
 * a right-aligned follower count. What differed was only drift — the follower figure was mono 13px on one
 * surface and non-mono 18px on the other, the rule was `line` vs `line-subtle`, the scroller capped at 452px
 * vs 462px, one carried the source badge and one did not, and each resolved cover art its own way.
 *
 * Every value below is the reference's (`roster-shared.css:663-679`), which is what settled those:
 * `.pl-fol__n` IS mono, `.pl-list` IS 462px, and `.pl-badge--sp` exists — so the badge is parity, not a new
 * invention, and the Streaming page was the surface missing it.
 *
 * Cover art goes through `ReleaseArtwork`, not a local `<img>`: it already owns the deterministic-gradient
 * fallback, so a 403 from Spotify's image CDN (which DOES 403 hotlinked covers) degrades to the placeholder
 * instead of a broken-image glyph. That fallback was previously re-implemented here as a local `PlaylistArt`.
 */
export function TopPlaylistList({
  playlists,
  showSource = true,
}: {
  playlists: readonly TopPlaylist[];
  /**
   * Render the platform badge on each row. On by default because the reference does
   * (`.pl-badge--sp`). Only worth turning off where the surrounding card already names the platform on
   * every row — pass it deliberately, don't fork the row.
   */
  showSource?: boolean;
}) {
  return (
    <ul className={styles.list}>
      {playlists.map((p, i) => (
        <li key={`${p.name}-${i}`} className={styles.row}>
          <ReleaseArtwork size="chip" className={styles.art} seed={p.name ?? ""} imageUrl={p.image} />
          <div className={styles.main}>
            <p className={styles.name} title={p.name ?? ""}>
              {p.name}
            </p>
            {showSource && (
              <div className={styles.tags}>
                {/* The badge is the CARD's platform: only Spotify placements are landed. The EDITORIAL badge
                    follows it, as in the reference (`.pl-badge--ed`) — Spotify-curated lists, classified
                    server-side (ART-273). The AGE stamp is the reference's "14d ago" (`.tpl__age`), back now the
                    list is the band's recent adds with their add dates (ART-283). */}
                <span className={styles.badge} data-platform="spotify">
                  Spotify
                </span>
                {p.editorial && (
                  <span className={styles.badge} data-kind="editorial">
                    Editorial
                  </span>
                )}
                {daysAgo(p.addedOn) && <span className={styles.age}>{daysAgo(p.addedOn)}</span>}
                {/* Which of the band's tracks is on it, and where (the reference's "featuring Same Side · #1"). */}
                {p.trackName && (
                  <span className={styles.feat}>
                    featuring <b>{p.trackName}</b>
                    {p.position != null ? ` · #${p.position}` : ""}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={styles.followers}>
            {/* `compactNumber`, never a raw count: a six-figure follower total blows the column out. */}
            <span className={styles.count}>{p.followers != null ? compactNumber(p.followers) : "—"}</span>
            <span className={styles.label}>Followers</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** "14d ago" from an add date — the reference's age stamp (legacy `daysAgoLabel`); empty when there is no date. */
function daysAgo(addedOn: string | null | undefined, now: number = Date.now()): string {
  const at = addedOn ? Date.parse(addedOn) : NaN;
  if (!Number.isFinite(at)) return "";
  return `${Math.max(0, Math.round((now - at) / 86_400_000))}d ago`;
}

